/**
 * OptimizedImage — high-performance image component for VillaApp
 *
 * Features:
 * - Lazy loading via native loading="lazy"
 * - Appwrite images served via /view (no transformation quota cost)
 * - Non-Appwrite URLs proxied through wsrv.nl CDN for resize + WebP
 * - Shimmer placeholder until image loads
 * - Proper width/height to prevent layout shift (CLS)
 * - Works with Appwrite URLs, Firebase Storage URLs, and any public URL
 */
import React, { useState, useRef, useEffect, memo } from 'react';
import { IMAGE_URL } from '../lib/config';

// ─── URL Optimization ──────────────────────────────────────────────

/**
 * Given any image URL, return an optimized version.
 * - R2 (images.villakosh.in) URLs → pass through (already optimal)
 * - Legacy Appwrite URLs → rewrite to R2
 * - Firebase / any public URL → wsrv.nl CDN proxy (resize + WebP)
 * - Local /placeholder → pass through
 */
export function getOptimizedUrl(url, width = 400, height = 0, quality = 80) {
  if (!url || url.startsWith('/') || url.startsWith('data:')) return url;

  // Already an R2 image URL — serve as-is.
  if (url.startsWith(IMAGE_URL) || url.includes('images.villakosh.in')) {
    return url;
  }

  // Legacy Appwrite Storage URL → rewrite to the R2 Image Worker.
  if (url.includes('/storage/buckets/') && url.includes('/files/')) {
    const fileIdMatch = url.match(/\/files\/([^/]+)\/(?:view|preview|download)/);
    if (fileIdMatch) {
      return `${IMAGE_URL}/files/${fileIdMatch[1]}`;
    }
  }

  // Any other public URL (Firebase Storage, Pixabay, etc.)
  // → Route through wsrv.nl free image CDN for resize + WebP.
  return buildCdnProxyUrl(url, width, height, quality);
}

/**
 * wsrv.nl — free, fast image CDN proxy.
 * Resizes + converts any public image URL to WebP on-the-fly.
 * No API key needed, supports Firebase Storage URLs.
 * Docs: https://wsrv.nl/docs/
 */
function buildCdnProxyUrl(originalUrl, width, height, quality) {
  const params = new URLSearchParams();
  params.set('url', originalUrl);
  if (width > 0) params.set('w', String(width));
  if (height > 0) params.set('h', String(height));
  params.set('fit', 'cover');
  params.set('output', 'webp');
  if (quality > 0 && quality < 100) params.set('q', String(quality));
  // Return optimized URL served from wsrv.nl edge CDN
  return `https://wsrv.nl/?${params.toString()}`;
}

// ─── Presets for common sizes ───────────────────────────────────────

export const IMAGE_SIZES = {
  // Card thumbnails on homepage
  cardSmall: { width: 320, height: 200, quality: 75 },
  cardMedium: { width: 480, height: 300, quality: 80 },
  cardLarge: { width: 640, height: 400, quality: 80 },

  // Villa detail page
  heroLarge: { width: 1200, height: 800, quality: 85 },
  heroMedium: { width: 800, height: 600, quality: 80 },
  gallery: { width: 600, height: 400, quality: 80 },

  // Tiny destination circles
  avatar: { width: 160, height: 160, quality: 70 },

  // Profile / small icons
  icon: { width: 80, height: 80, quality: 70 },
};

// ─── Component ──────────────────────────────────────────────────────

const OptimizedImage = memo(function OptimizedImage({
  src,
  alt = '',
  className = '',
  width,
  height,
  quality = 80,
  preset,           // e.g. "cardSmall", "heroLarge"
  eager = false,    // true = load immediately (above-fold hero images)
  onClick,
  style,
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  // Resolve preset
  const p = preset ? IMAGE_SIZES[preset] : null;
  const w = width || p?.width || 400;
  const h = height || p?.height || 0;
  const q = quality || p?.quality || 80;

  const optimizedSrc = getOptimizedUrl(src, w, h, q);

  // Reset state when effective image URL changes
  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
  }, [optimizedSrc]);

  useEffect(() => {
    const imageElement = imgRef.current;
    if (!imageElement) return;

    if (imageElement.complete && imageElement.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [optimizedSrc]);

  if (!src || hasError) {
    return (
      <div
        className={`bg-gray-200 flex items-center justify-center ${className}`}
        style={style}
        aria-label={alt}
      >
        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>
    );
  }

  // Render in a shared container so skeleton and image overlap.
  // This prevents skeleton blocks from pushing images out of view in fixed-height cards.
  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={style}
      onClick={onClick}
    >
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" aria-hidden="true" />
      )}
      <img
        ref={imgRef}
        src={optimizedSrc}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding={eager ? 'sync' : 'async'}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={() => setIsLoaded(true)}
        onError={() => setHasError(true)}
        width={w}
        height={h || undefined}
      />
    </div>
  );
});

export default OptimizedImage;
